import { formatPhoneForDisplay } from '../../utils/format-phone.util';

const DAY_MS = 24 * 60 * 60 * 1000;
const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

function payloadString(payload: Record<string, unknown>, key: string): string | undefined {
  const value = payload[key];
  if (value === undefined || value === null || value === '') {
    return undefined;
  }
  return String(value);
}

function parseDateOnly(value: string): { year: number; month: number; day: number } {
  const match = DATE_ONLY_PATTERN.exec(value);
  if (!match) {
    throw new Error(`Invalid date: ${value}`);
  }

  return {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
  };
}

export function formatDateInput(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Local calendar start-of-day converted to a UTC ISO instant for API filtering. */
export function localDateStartToUtcIso(dateOnly: string): string {
  const { year, month, day } = parseDateOnly(dateOnly);
  return new Date(year, month - 1, day, 0, 0, 0, 0).toISOString();
}

/** Local calendar end-of-day converted to a UTC ISO instant for API filtering. */
export function localDateEndToUtcIso(dateOnly: string): string {
  const { year, month, day } = parseDateOnly(dateOnly);
  return new Date(year, month - 1, day, 23, 59, 59, 999).toISOString();
}

export function getDefaultDateRange(days = 7): { fromDate: string; toDate: string } {
  const to = new Date();
  const from = new Date(to.getTime() - (days - 1) * DAY_MS);
  return {
    fromDate: formatDateInput(from),
    toDate: formatDateInput(to),
  };
}

export function formatTraceTimestamp(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  });
}

/** Formats the span between startedAt and endedAt as e.g. "1:05" or "12:03". Returns undefined if either timestamp is missing/invalid or the call has zero/negative duration. */
export function formatTraceDuration(startedAt?: string, endedAt?: string): string | undefined {
  if (!startedAt || !endedAt) {
    return undefined;
  }

  const start = new Date(startedAt).getTime();
  const end = new Date(endedAt).getTime();
  if (Number.isNaN(start) || Number.isNaN(end) || end <= start) {
    return undefined;
  }

  const totalSeconds = Math.round((end - start) / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

export function getTraceDisplayName(trace: {
  callerName?: string;
  phone?: string;
  cxoneContactId?: string;
}): string {
  if (trace.callerName) {
    return trace.callerName;
  }
  if (trace.phone) {
    return formatPhoneForDisplay(trace.phone);
  }
  if (trace.cxoneContactId) {
    return trace.cxoneContactId;
  }
  return 'Unknown';
}

export interface TraceSummaryFromEvents {
  callerName?: string;
  phone?: string;
  direction?: string;
  cxoneContactId?: string;
  startedAt?: string;
}

/** Derive list-style trace summary fields from raw call activity events. */
export function extractTraceSummaryFromEvents(
  events: {
    eventType: string;
    payload?: Record<string, unknown>;
    cxoneContactId?: string;
    createdAt: string;
  }[]
): TraceSummaryFromEvents {
  const summary: TraceSummaryFromEvents = {};

  for (const event of events) {
    const payload = event.payload ?? {};

    if (event.eventType === 'CALL_RECEIVED') {
      summary.direction = payloadString(payload, 'direction') ?? summary.direction;
      summary.phone = payloadString(payload, 'phone') ?? summary.phone;
      summary.callerName = payloadString(payload, 'callerName') ?? summary.callerName;
    }

    if (event.eventType === 'CALL_ANSWERED') {
      summary.direction = payloadString(payload, 'direction') ?? summary.direction;
      summary.phone = payloadString(payload, 'phone') ?? summary.phone;
    }

    if (event.eventType === 'CALL_ENDED') {
      summary.direction = payloadString(payload, 'direction') ?? summary.direction;
      summary.phone = payloadString(payload, 'phone') ?? summary.phone;
      summary.callerName = payloadString(payload, 'callerName') ?? summary.callerName;
    }

    if (event.eventType === 'DISPOSITION_COMPLETED') {
      summary.direction = payloadString(payload, 'direction') ?? summary.direction;
      summary.phone = payloadString(payload, 'phone') ?? summary.phone;
    }

    summary.cxoneContactId = event.cxoneContactId ?? summary.cxoneContactId;
  }

  if (events.length > 0) {
    summary.startedAt = events[0].createdAt;
  }

  return summary;
}
